import type { ReactNode } from 'react';
import { View } from 'react-native';

import { isTerminalFailureStep, pickActiveStep } from '@/components/deal-stepper/pickActiveStep';
import { SERVER_STEP_STATE, type Action, type Deal, type DealCta, type DealStep, type StepStateEntry } from '@/types/deal';

import type { VideoHandlers } from './SocialStepVideos';
import { SocialStepRow, type Look } from './SocialStepRow';

export type SocialStepperProps = {
  deal: Deal;
  action?: Action;
  onCtaPress: (step: DealStep, cta?: DealCta) => void;
  ctaLoading?: boolean;
  /** The upload step's videos: stills, Replace / Remove, the hero's scroll target. */
  videoHandlers?: VideoHandlers;
  /** The upload step's uploader (SocialInlineUpload), given to the step whose button uploads. */
  uploader?: ReactNode;
  /** "Earned so far" (SocialEarningsCard), shown in the Get paid step. */
  earnings?: ReactNode;
  testID?: string;
};

/**
 * MarketPryce Social's "Your Deal Progress" list (Figma "Apply" frame): numbered rings,
 * a filled green check once done, muted future steps that still show what
 * they ask for. The server decides every state (`action.step_states`); this
 * maps them onto four looks:
 *
 *   done     confirmed / completed          green check, folded to its name (See opens it)
 *   active   active                         green-tinted ring, copy, the step's button
 *   pending  waiting / reported / overdue   green-tinted ring, copy, a grey button
 *                                           carrying the server's label ("Preparing to ship")
 *   locked   locked (or nothing sent)       grey ring, muted copy
 *
 * The server can open a step ahead of its turn (the playbook while the
 * product ships), so a step's button follows its own state, never "the
 * active step". Each row's details (tracker, videos, a coach's feedback) are
 * the server's too: see SocialStepRow. A rejected/expired action shows its
 * terminal step alone.
 */
/** The Social template's "Get paid" step (Voltr::DealTemplate). */
const PAID_STEP = 'paid';

export function SocialStepper({ deal, action, onCtaPress, ctaLoading, videoHandlers, uploader, earnings, testID }: SocialStepperProps) {
  const steps = deal.steps_config.steps;
  const activeStep = pickActiveStep(steps, action);

  if (activeStep && isTerminalFailureStep(activeStep, action)) {
    return (
      <View testID={testID}>
        <SocialStepRow step={activeStep} number={steps.indexOf(activeStep) + 1} look="failed" isLast />
      </View>
    );
  }

  const visible = steps.filter((s) => !s.terminal);
  return (
    <View testID={testID}>
      {visible.map((step, i) => {
        const entry = action?.step_states?.[step.key];
        const look = lookFor(step, entry, activeStep, action);
        return (
          <SocialStepRow
            key={step.key}
            step={step}
            number={i + 1}
            look={look}
            entry={entry}
            // Active: the step's ask. Once there are videos, "Add another video" works in any state.
            onCtaPress={look === 'active' || entry?.add_label ? (cta) => onCtaPress(step, cta) : undefined}
            ctaLoading={ctaLoading}
            videoHandlers={videoHandlers}
            trackHref={{ pathname: '/deals/track/[slug]', params: { slug: deal.slug } }}
            uploader={step.ctas?.some((c) => c.action === 'voltr_upload') ? uploader : undefined}
            extra={step.key === PAID_STEP ? earnings : undefined}
            isLast={i === visible.length - 1}
          />
        );
      })}
    </View>
  );
}

function lookFor(step: DealStep, entry: StepStateEntry | undefined, activeStep: DealStep | undefined, action?: Action): Look {
  // Before the first tap there is no Action and no step_states: step 1 is the ask.
  if (!action) return step === activeStep ? 'active' : 'locked';
  switch (entry?.state) {
    case SERVER_STEP_STATE.Confirmed:
    case SERVER_STEP_STATE.Completed:
      return 'done';
    case SERVER_STEP_STATE.Active:
      return 'active';
    case SERVER_STEP_STATE.Waiting:
    case SERVER_STEP_STATE.Reported:
    case SERVER_STEP_STATE.Overdue:
      return 'pending';
    default:
      return 'locked';
  }
}
